<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxBadge,
  NxBanner,
  NxButton,
  NxSettingsToggle,
  NxTable,
  NxTableColumn,
  downloadBlob,
} from '@blpde/nx-ui';
import {
  spellCorrectionEntries,
  spellCorrectionRemoval,
  type SpellCheckConfig,
  type SpellCorrectionEntry,
} from '../../config/spellCheck';
import { correctionsCsvText, parseCorrectionsCsv, type DictionaryLanguage } from '../dictionaryFile';
import { dictionaryFold } from '../dictionaryFold';
import { correctionsImportPreview } from '../dictionaryImport';
import type { DictionaryLevelHandle, LearnCorrectionsSwitch } from './level';
import { DICTIONARY_TEXT, languageLabel, originLabel, originVariant } from './labels';
import { correctionDraftPatch, correctionDraftProblem, type CorrectionDraft } from './drafts';
import { DICTIONARY_PAGE_SIZE, usePagedRows } from './paging';
import { useDictionaryTabState } from './tabState';
import CorrectionEditorPanel from './CorrectionEditorPanel.vue';
import DictionaryFillHint from './DictionaryFillHint.vue';
import DictionaryImportModal from './DictionaryImportModal.vue';
import DictionaryToolbar from './DictionaryToolbar.vue';
import ExplainPanel from './ExplainPanel.vue';

/**
 * „Korrekturen“ (I-3024): the wrong words and what the search suggests
 * instead — `spell_check.corrections` of one layer, one target per word and
 * language. The rows of a farther layer show their origin (Firma, Mandanten-Container)
 * and stay as they are; a layer before the written one that locks the field
 * makes the whole tab read-only. A side panel edits one correction, the
 * file import and export are CSV (`falsch;richtig;sprache`). The switch of
 * `search.learnCorrections` sits on top where the host brings it (`learn`):
 * the same field as the setting in the search section.
 */
const props = defineProps<{
  level: DictionaryLevelHandle<SpellCheckConfig>;
  /** The switch row of `search.learnCorrections`; the hub brings it, the admin views do not. */
  learn?: LearnCorrectionsSwitch;
}>();

const { t } = useI18n();
const T = DICTIONARY_TEXT;
const FIELD = 'corrections';
const state = useDictionaryTabState(props.level as DictionaryLevelHandle<unknown>, FIELD);

interface Row extends SpellCorrectionEntry, Record<string, unknown> {
  id: string;
}

const search = ref('');
const lang = ref<DictionaryLanguage | 'all'>('all');
const entries = computed(() =>
  spellCorrectionEntries(props.level.effective.value, props.level.layers.value, props.level.target.value),
);
const ownEntries = computed(() => entries.value.filter((entry) => entry.source === 'own'));
const rows = computed<Row[]>(() => {
  const needle = dictionaryFold(search.value.trim());
  return entries.value
    .filter((entry) => lang.value === 'all' || entry.lang === lang.value)
    .filter(
      (entry) =>
        !needle || dictionaryFold(entry.typed).includes(needle) || dictionaryFold(entry.to).includes(needle),
    )
    .map((entry) => ({ ...entry, id: `${entry.lang}:${entry.typed}` }));
});
const paged = usePagedRows(() => rows.value);
const table = paged.table;
watch([search, lang], () => paged.reset());

function asRow(value: unknown): Row {
  return value as Row;
}

const editable = computed(() => !state.readOnly.value && state.layersKnown.value);

/* ── Editing ── */

interface Draft extends CorrectionDraft {
  editing: { lang: DictionaryLanguage; typed: string } | null;
  key: number;
}
const draft = ref<Draft | null>(null);
let draftKey = 0;

function openNew(): void {
  draft.value = {
    typed: '',
    to: '',
    lang: lang.value === 'en' ? 'en' : 'de',
    editing: null,
    key: (draftKey += 1),
  };
}

function openEdit(row: Row): void {
  draft.value = {
    typed: row.typed,
    to: row.to,
    lang: row.lang,
    editing: { lang: row.lang, typed: row.typed },
    key: (draftKey += 1),
  };
}

function problemOf(next: CorrectionDraft) {
  return correctionDraftProblem(next, ownEntries.value, draft.value?.editing ?? undefined);
}

function save(next: CorrectionDraft) {
  return props.level.patch(correctionDraftPatch(next, draft.value?.editing ?? undefined));
}

/** Only an own row of an editable layer can be changed; a farther layer's row stays as it is. */
function isOwnEditable(row: Row): boolean {
  return editable.value && row.source === 'own';
}

/** Double-click (or Enter) on a row opens the side panel of an own row; an inherited row stays read-only. */
function onRowOpen(_rowId: string, _columnId: string, row: Row): void {
  if (isOwnEditable(row)) openEdit(row);
}

/**
 * The kit's pinned X (after its confirmation). `false` keeps the row in the
 * table: a refused write already showed the standard write-failure toast.
 */
async function onDelete({ id }: { id: string }): Promise<boolean> {
  const row = rows.value.find((entry) => entry.id === id);
  if (!row || !isOwnEditable(row)) return false;
  return state.write(spellCorrectionRemoval(row.lang, row.typed));
}

/* ── Import and export ── */

const importOpen = ref(false);

function previewImport(text: string) {
  const defaults: DictionaryLanguage = lang.value === 'en' ? 'en' : 'de';
  return correctionsImportPreview(parseCorrectionsCsv(text, defaults).rows, state.ownDocument.value ?? {});
}

function exportFile(): void {
  downloadBlob(correctionsCsvText(ownEntries.value), t(`${T}.export.corrections`));
}

const levelLabel = computed(() => t(`${T}.level.${props.level.key}`));
</script>

<template>
  <div class="dict" :class="{ 'dict--editor': draft }" data-testid="dictionary-corrections">
    <div class="dict-main">
      <p class="dict-note">{{ t(`${T}.hints.corrections`) }}</p>
      <div v-if="learn" data-testid="dictionary-learn">
        <NxSettingsToggle
          :model-value="learn.value"
          :label="t(`${T}.learn.label`)"
          :disabled="learn.locked || learn.loading"
          @update:model-value="(on: boolean) => learn!.set(on)"
        />
      </div>
      <p v-if="learn?.loadFailed" class="dict-note" data-testid="dictionary-learn-load-failed">
        {{ t(`${T}.learn.loadFailed`) }}
        <NxButton variant="link" data-testid="dictionary-learn-retry" @click="learn!.retry()">
          {{ t(`${T}.learn.retry`) }}
        </NxButton>
      </p>
      <p v-if="learn?.locked" class="dict-note" data-testid="dictionary-learn-locked">{{ t(`${T}.learn.locked`) }}</p>
      <p v-if="learn" class="dict-note">{{ t(`${T}.learn.hint`) }}</p>
      <NxBanner
        v-if="state.readOnlyText.value"
        variant="info"
        :title="state.readOnlyText.value"
        data-testid="dictionary-readonly"
      />
      <NxSettingsToggle
        v-if="level.setLock"
        :model-value="state.ownLocked.value"
        :label="t(`${T}.lock.label`)"
        :disabled="!editable"
        data-testid="dictionary-lock"
        @update:model-value="(on: boolean) => state.setOwnLock(on)"
      />
      <DictionaryToolbar v-model:search="search" v-model:lang="lang">
        <NxButton data-testid="dictionary-add-correction" :disabled="!editable" @click="openNew">
          {{ t(`${T}.actions.addCorrection`) }}
        </NxButton>
        <NxButton variant="outline" data-testid="dictionary-import" :disabled="!editable" @click="importOpen = true">
          {{ t(`${T}.actions.import`) }}
        </NxButton>
        <NxButton variant="outline" data-testid="dictionary-export" :disabled="!ownEntries.length" @click="exportFile">
          {{ t(`${T}.actions.export`) }}
        </NxButton>
      </DictionaryToolbar>
      <div class="dict-rows">
        <!-- @vue-generic {Row} -->
        <NxTable
          ref="table"
          :data="paged.pageRows.value as Row[]"
          :total-count="paged.total.value"
          :row-id-key="(row: Row) => row.id"
          :pagination-limit="DICTIONARY_PAGE_SIZE"
          :pagination-page-sizes="[DICTIONARY_PAGE_SIZE]"
          pagination-mode="pages"
          hide-create-button
          :on-delete="onDelete"
          :row-deletable="isOwnEditable"
          :on-table-state-change="paged.onState"
          @cell-double-click="onRowOpen"
        >
          <NxTableColumn
            column-id="typed"
            :display-name="t(`${T}.columns.typed`)"
            :filterable="false"
            :min-width="180"
            :to-csv-string="(row: Row) => row.typed"
          >
            <template #cell="{ rowData }">
              <span data-testid="dictionary-typed">{{ asRow(rowData).typed }}</span>
            </template>
          </NxTableColumn>
          <NxTableColumn
            column-id="to"
            :display-name="t(`${T}.columns.target`)"
            :filterable="false"
            :min-width="180"
            :to-csv-string="(row: Row) => row.to"
          >
            <template #cell="{ rowData }">
              <span data-testid="dictionary-target">{{ asRow(rowData).to }}</span>
            </template>
          </NxTableColumn>
          <NxTableColumn
            column-id="lang"
            :display-name="t(`${T}.columns.language`)"
            :filterable="false"
            :to-csv-string="(row: Row) => row.lang"
          >
            <template #cell="{ rowData }">{{ languageLabel(asRow(rowData).lang) }}</template>
          </NxTableColumn>
          <NxTableColumn
            column-id="origin"
            :display-name="t(`${T}.columns.origin`)"
            :filterable="false"
            :min-width="140"
            :to-csv-string="(row: Row) => originLabel(t, row)"
          >
            <template #cell="{ rowData }">
              <NxBadge
                :variant="originVariant(asRow(rowData))"
                :title="asRow(rowData).source === 'own' ? undefined : t(`${T}.origin.inheritedHint`)"
                data-testid="dictionary-origin"
              >
                {{ originLabel(t, asRow(rowData)) }}
              </NxBadge>
            </template>
          </NxTableColumn>
        </NxTable>
      </div>
      <DictionaryFillHint :document="state.ownDocument.value" kind="spell" />
    </div>
    <CorrectionEditorPanel
      v-if="draft"
      :key="draft.key"
      :editing="draft.editing"
      :typed="draft.typed"
      :to="draft.to"
      :lang="draft.lang"
      :level-label="levelLabel"
      :problem="problemOf"
      :save="save"
      @close="draft = null"
      @reload="draft = null"
    />
    <ExplainPanel v-else />
    <DictionaryImportModal
      v-model:open="importOpen"
      :title="t(`${T}.import.titleCorrections`)"
      :hint="t(`${T}.import.formatCorrections`)"
      accept=".csv,.txt,text/csv,text/plain"
      :preview="previewImport"
      :apply="(patch) => level.patch(patch)"
    />
  </div>
</template>

<style scoped>
.dict {
  display: grid;
  grid-template-columns: minmax(0, 1fr) calc(var(--nxt-size-unit) * 23.75);
  gap: var(--nxt-space-sm);
  align-items: start;
}
.dict--editor {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
}
.dict-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}
.dict-note {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.dict-rows {
  height: calc(var(--nxt-size-unit) * 26.875);
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
